import * as React from 'react';

import cx from 'classnames';
import FocusLock from 'react-focus-lock';

import { translate } from '~l10n/client';
import { Box } from '~shared/components/Box';
import { Portal } from '~shared/components/Portal/Portal';

import { ariaHideOthers } from './ariaHideOthers';
import { DialogContent, DialogContentProps } from './DialogContent';
import { DialogCloseProvider } from './useDialogClose';
import {
  DialogAction,
  DialogState,
  useDialogReducer,
} from './useDialogReducer';

export { DialogHeaderProps } from './DialogContent';

const ANIMATION_DURATION = 300;

interface DialogPortalProps {
  children: React.ReactNode;
}

function DialogPortal({ children }: DialogPortalProps) {
  const [dialogElement, setElement] = React.useState<HTMLElement | null>(null);

  React.useEffect(() => {
    const dialogContainerId = 'dialog-container';
    let element = document.getElementById(dialogContainerId);

    if (!element) {
      element = document.createElement('div');
      element.setAttribute('id', dialogContainerId);
      element.setAttribute('class', 'fixed z-100');

      document.body.appendChild(element);
    }

    setElement(element);
  }, []);

  if (!dialogElement) return null;

  return <Portal container={dialogElement}>{children}</Portal>;
}

export interface DialogProps
  extends Omit<
    DialogContentProps,
    'onClose' | 'scrollLock' | 'headingRef' | 'dialogRef'
  > {
  open: boolean;
  onClose: onCloseDialog;
  showBackdrop?: boolean;
  /** When true, clicking the backdrop won't close the dialog. Defaults to false. */
  disableBackdropClose?: boolean;
  /** When true, pressing Escape won't close the dialog. Defaults to false. */
  disableEscapeClose?: boolean;
}

export type onCloseDialog = (openState: false) => void;

/**
 * Basic, but customisable modal popup component.
 *
 * Will handle the title and close button itself by default, but render props
 * are available for using a custom header. The default close button can be
 * removed by passing the `hasCustomCloseButton` prop if a custom header
 * includes its own close button, e.g. a "Done" button.
 *
 * When using a `size` value other than `default` or `content`, the dialog will
 * expand to full height below the `sm` breakpoint.
 *
 * Dialogs should only be used as a last resort when other in-flow alternatives
 * are not suitable.
 *
 * @see https://www.w3.org/TR/wai-aria-practices-1.2/#dialog_modal
 */
export function Dialog({
  children,
  id,
  size,
  padding,
  position = 'center',
  __customClassEscapeHatch,
  title,
  description,
  closeLabel = translate('Close'),
  hasCustomCloseButton,
  open,
  onClose,
  showBackdrop = true,
  titleWeight = 'bold',
  disableBackdropClose = false,
  disableEscapeClose = false,
}: DialogProps) {
  const [trapActive, setTrapActive] = React.useState(true);
  const [dialogState, dispatchDialogAction] = useDialogReducer(
    open ? DialogState.OPEN : DialogState.CLOSED
  );

  const shouldFocus =
    typeof document !== 'undefined' &&
    typeof document.hasFocus === 'function' &&
    document.hasFocus();
  const isDrawer = position === 'right' || position === 'left';

  const openRef = React.useRef(open);
  const dialogRef = React.useRef<HTMLElement>(null);
  const headingRef = React.useRef<HTMLElement>(null);
  const closeHandlerRef = React.useRef<DialogProps['onClose']>(onClose);

  const initiateClose = React.useCallback(() => {
    dispatchDialogAction(DialogAction.BeginClose);
  }, [dispatchDialogAction]);

  const handleBackdropClose = React.useCallback(() => {
    if (!disableBackdropClose) {
      dispatchDialogAction(DialogAction.BeginClose);
    }
  }, [disableBackdropClose, dispatchDialogAction]);

  React.useEffect(() => {
    openRef.current = open;
    dispatchDialogAction(
      open ? DialogAction.BeginOpen : DialogAction.BeginClose
    );
  }, [open, dispatchDialogAction]);

  React.useEffect(() => {
    if (dialogState === DialogState.CLOSING) {
      const timer = setTimeout(() => {
        dispatchDialogAction(DialogAction.FinishClose);
      }, ANIMATION_DURATION);

      return () => clearTimeout(timer);
    }

    if (dialogState === DialogState.OPENING) {
      const timer = setTimeout(() => {
        dispatchDialogAction(DialogAction.FinishOpen);
      }, ANIMATION_DURATION);

      return () => clearTimeout(timer);
    }

    if (dialogState === DialogState.CLOSED && openRef.current) {
      closeHandlerRef.current(false);
    }
  }, [dialogState, dispatchDialogAction]);

  const shouldAriaHideOthers =
    dialogState === DialogState.OPEN || dialogState === DialogState.CLOSING;

  React.useEffect(() => {
    if (shouldAriaHideOthers && dialogRef.current) {
      return ariaHideOthers(dialogRef.current, { delay: ANIMATION_DURATION });
    }
  }, [shouldAriaHideOthers]);

  React.useEffect(() => {
    if (typeof onClose === 'function') {
      closeHandlerRef.current = onClose;
    }
  }, [onClose]);

  React.useEffect(() => {
    const event = trapActive ? 'blur' : 'focus';
    const handleEvent = () => setTrapActive(!trapActive);

    window.addEventListener(event, handleEvent);

    return () => window.removeEventListener(event, handleEvent);
  }, [trapActive]);

  // Global escape key handler - works regardless of focus position within the dialog
  React.useEffect(() => {
    const isDialogVisible =
      dialogState === DialogState.OPENING || dialogState === DialogState.OPEN;
    if (!isDialogVisible || disableEscapeClose) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        initiateClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [dialogState, disableEscapeClose, initiateClose]);

  const backdropClass = cx(
    [
      'fixed inset-0 z-100 transition ease-in-out',
      dialogState !== DialogState.OPEN ? 'opacity-0' : 'opacity-66',
      dialogState === DialogState.CLOSING ? 'duration-200' : 'duration-300',
      dialogState === DialogState.CLOSING && 'pointer-events-none',
    ],
    {
      ['bg-grey-200']: showBackdrop,
    }
  );

  const contentClass = cx([
    'fixed inset-0 z-100 pointer-events-none transition',
    isDrawer ? 'duration-200 lg:duration-150' : 'duration-200',
    position === 'center' && 'sm:p-4',
    position === 'top' && 'sm:p-4 sm:py-screen-h-1/7',
    position === 'right' && 'lg:pl-6',
    position === 'left' && 'lg:pr-6',

    dialogState !== DialogState.OPEN && [
      'transform',
      isDrawer ? 'lg:opacity-0' : 'opacity-0 scale-95',
      position === 'right' && 'translate-x-full lg:translate-x-16',
      position === 'left' && '-translate-x-full lg:-translate-x-16',
    ],
    dialogState === DialogState.OPENING && 'ease-out',
    dialogState === DialogState.CLOSING && 'ease-in',
  ]);

  return dialogState === DialogState.OPENING ||
    dialogState === DialogState.OPEN ||
    dialogState === DialogState.CLOSING ? (
    <DialogPortal>
      <FocusLock
        disabled={!trapActive}
        autoFocus={false}
        returnFocus
        onActivation={() => {
          if (dialogState === DialogState.OPEN) return;
          if (headingRef.current && shouldFocus) {
            headingRef.current.focus();
          }
        }}
      >
        <Box
          data-testid="dialog-backdrop"
          className={backdropClass}
          onClick={
            dialogState === DialogState.OPEN ? handleBackdropClose : undefined
          }
        />

        <DialogCloseProvider closeAction={initiateClose}>
          <Box className={contentClass}>
            <DialogContent
              id={id}
              size={size}
              padding={padding}
              position={position}
              __customClassEscapeHatch={__customClassEscapeHatch}
              title={title}
              description={description}
              onClose={initiateClose}
              closeLabel={closeLabel}
              hasCustomCloseButton={hasCustomCloseButton}
              scrollLock={!(dialogState === DialogState.CLOSING)}
              dialogRef={dialogRef}
              headingRef={headingRef}
              titleWeight={titleWeight}
            >
              {children}
            </DialogContent>
          </Box>
        </DialogCloseProvider>
      </FocusLock>
    </DialogPortal>
  ) : null;
}
